import React, { useEffect } from 'react';
import { X, Download, AlertCircle } from 'lucide-react';

const AttachmentPreviewModal = ({ attachment, previewUrl, onClose }) => {
    useEffect(() => {
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = previewUrl;
        link.download = attachment.filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderPreview = () => {
        if (!previewUrl) {
            return (
                <div className="flex items-center justify-center h-64 bg-stone-100 rounded-lg">
                    <div className="text-center">
                        <AlertCircle className="w-12 h-12 text-stone-400 mx-auto mb-2" />
                        <p className="text-stone-500">Preview not available</p>
                    </div>
                </div>
            );
        }

        if (attachment.mimeType?.startsWith('image/')) {
            return (
                <img
                    src={previewUrl}
                    alt={attachment.filename}
                    className="max-w-full max-h-96 object-contain rounded-lg"
                    onError={() => {
                        // Handle image load error
                        console.error('Failed to load image preview');
                    }}
                />
            );
        }

        if (attachment.mimeType === 'application/pdf') {
            return (
                <iframe
                    src={previewUrl}
                    className="w-full h-96 border rounded-lg"
                    title={attachment.filename}
                />
            );
        }

        if (attachment.mimeType?.startsWith('text/')) {
            return (
                <iframe
                    src={previewUrl}
                    className="w-full h-96 border rounded-lg bg-white"
                    title={attachment.filename}
                />
            );
        }

        // Fallback for unsupported types
        return (
            <div className="flex items-center justify-center h-64 bg-stone-100 rounded-lg">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-stone-400 mx-auto mb-2" />
                    <p className="text-stone-500">Preview not supported for this file type</p>
                    <button
                        onClick={handleDownload}
                        className="mt-2 px-4 py-2 bg-stone-800 text-white rounded hover:bg-stone-700 transition-colors"
                    >
                        Download File
                    </button>
                </div>
            </div>
        );
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-stone-200">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-stone-900 truncate" title={attachment.filename}>
                            {attachment.filename}
                        </h3>
                        <p className="text-sm text-stone-500">
                            {attachment.mimeType} • {formatFileSize(attachment.size)}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                        <button
                            onClick={handleDownload}
                            className="p-2 text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded transition-colors"
                            title="Download"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded transition-colors"
                            title="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 overflow-auto max-h-96">
                    {renderPreview()}
                </div>
            </div>
        </div>
    );
};

export default AttachmentPreviewModal;